/**
 * R267c · Feb 2026 — Interakt / Meta WhatsApp error code legend.
 *
 * Aquarius Global (prod) users see "failed" chips on their bulk-WA
 * campaigns but the raw Interakt/Meta error code (`470`, `131047`,
 * `132000` …) doesn't tell them what to do next.  This map turns
 * those codes into a plain-English tip + actionable fix so ops can
 * triage without opening Interakt's dashboard.
 *
 * Sources:
 *   - Meta Business Cloud API error reference (v20.0+).
 *   - Interakt error code catalogue.
 * Codes not in the table fall back to a generic tip.
 */

// Both string and numeric keys are accepted so callers don't have
// to normalise types.
const _RAW = {
  // ─── Opt-out / policy ────────────────────────────────────────
  "131048": {
    label: "Spam-rate limit reached",
    tip: "Meta has throttled your number for high user-report volume. Pause the campaign, review the template copy, and try again after 24 h.",
  },
  "131049": {
    label: "Per-recipient rate limit",
    tip: "This customer received too many messages recently. Wait 24 h or reach them via another channel.",
  },
  "470": {
    label: "Customer opted out",
    tip: "Recipient blocked or unsubscribed on WhatsApp. Do not retry — remove them from the segment.",
  },
  "131050": {
    label: "Marketing opt-out",
    tip: "Recipient opted out of marketing template messages. Switch to a Utility template or drop them.",
  },
  // ─── 24-hour session / re-engagement ─────────────────────────
  "131047": {
    label: "Re-engagement required",
    tip: "The 24-hour customer service window closed. Send an approved template (not a free-form session message) to reopen the conversation.",
  },
  "131051": {
    label: "Unsupported message type",
    tip: "Meta rejected the message type. Verify template category and body match what was approved.",
  },
  // ─── Template mismatch ───────────────────────────────────────
  "132000": {
    label: "Template mismatch",
    tip: "The template variable count you sent doesn't match Meta's approved version. Re-sync templates and check {{1}}, {{2}}… slots.",
  },
  "132001": {
    label: "Template not found",
    tip: "Template name or language code is wrong. Verify the exact name/language in Interakt → Templates.",
  },
  "132005": {
    label: "Translated content mismatch",
    tip: "Body content doesn't match the approved translation. Update or re-submit for approval.",
  },
  "132007": {
    label: "Template rate-limit",
    tip: "Too many sends of this template in a short window. Stagger the campaign or pick a different template.",
  },
  "132012": {
    label: "Parameter format invalid",
    tip: "A variable is malformed (e.g. link, date, or currency in the wrong shape). Re-check body_vars.",
  },
  "132015": {
    label: "Template paused",
    tip: "Meta paused this template because of low user feedback. Pick another approved template.",
  },
  "132016": {
    label: "Template disabled",
    tip: "Template was disabled by Meta after repeated policy strikes. Use a different template.",
  },
  // ─── Numbers / account ───────────────────────────────────────
  "131026": {
    label: "Number not on WhatsApp",
    tip: "The recipient's phone isn't registered on WhatsApp. Verify the country code + number, or fall back to SMS/email.",
  },
  "131031": {
    label: "Account locked",
    tip: "Your WhatsApp Business Account is temporarily locked. Contact Interakt / Meta support.",
  },
  "131052": {
    label: "Media download failed",
    tip: "Meta could not fetch your template header media. Check the media URL is public, HTTPS, and under 100 MB.",
  },
  "131053": {
    label: "Meta couldn't decode the video",
    tip: "Meta rejected the video's codec/container. Re-encode as H.264 + AAC in an MP4 or 3GP wrapper, under 16 MB. Interakt recommends running the file through cloudconvert.com even if it's already .mp4 — many phone recordings use profiles Meta won't accept.",
  },
  // ─── Interakt-side wrapper codes (from our helper) ───────────
  "interakt_result_false": {
    label: "Interakt rejected the request",
    tip: "Interakt accepted the API call but flagged the message as invalid before it reached Meta. See detail for exact reason.",
  },
  "interakt_429": {
    label: "Rate-limited by Interakt",
    tip: "Too many API calls/sec. Increase the campaign stagger to 2 s or higher.",
  },
  "interakt_429_retries_exhausted": {
    label: "Rate-limited (3× retry)",
    tip: "Even after retries Interakt kept rate-limiting. Reduce concurrent campaigns or contact Interakt to raise your quota.",
  },
  "interakt_500_retries_exhausted": {
    label: "Interakt outage",
    tip: "Interakt's API returned 5xx three times. Try again in a few minutes.",
  },
  "interakt_502_retries_exhausted": {
    label: "Interakt gateway error",
    tip: "Upstream gateway failed. Retry the failed contacts once Interakt status page shows green.",
  },
  "interakt_503_retries_exhausted": {
    label: "Interakt unavailable",
    tip: "Interakt was unavailable. Retry from the campaign detail once their status page shows green.",
  },
  "interakt_network_retries_exhausted": {
    label: "Network error",
    tip: "Our server couldn't reach Interakt (DNS/TLS/timeout). Almost always transient — retry the failed contacts.",
  },
};

/**
 * Look up a friendly error explanation.
 * @param {string|number|null|undefined} code — Interakt/Meta error code.
 * @returns {{label:string, tip:string}}
 */
export function waErrorInfo(code) {
  if (code == null) return {
    label: "Failed",
    tip: "No error code returned. Check the raw campaign log for details.",
  };
  const key = String(code).trim();
  if (_RAW[key]) return _RAW[key];
  // Some Interakt reasons come as `interakt_<http_status>` — try that.
  const num = key.replace(/[^\d]/g, "");
  if (num && _RAW[num]) return _RAW[num];
  return {
    label: "Failed",
    tip: `No plain-English mapping for code "${key}". Check Interakt logs.`,
  };
}

/**
 * Parse a compact reason string from `_run_bulk_wa_job` which now
 * looks like `interakt_429 · http=429 · <detail>`. Extract the code
 * for the legend, and keep the rest as `detail`.
 * @param {string} reason
 */
export function parseWaReason(reason) {
  if (!reason) return { code: null, detail: "" };
  const parts = String(reason).split(/\s*·\s*/);
  const code = parts[0]?.trim() || null;
  const detail = parts.slice(1).join(" · ");
  return { code, detail };
}
